import React, { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import {
  Box, Typography, Button, TextField, CircularProgress,
  InputAdornment, MenuItem, Select as MuiSelect, FormControl,
} from '@mui/material';
import InventoryIcon from '@mui/icons-material/Inventory';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import LabelIcon from '@mui/icons-material/Label';
import QrCodeIcon from '@mui/icons-material/QrCode';
import PercentIcon from '@mui/icons-material/Percent';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import Select from 'react-select';

import { fetchData, postData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  accent: '#2dd4bf', accentDark: '#14b8a6',
  accentDim: 'rgba(45,212,191,0.10)', accentBorder: 'rgba(45,212,191,0.22)',
  bg: '#f0f4f4', card: '#ffffff', cardBorder: 'rgba(0,0,0,0.06)',
  text: '#1a2e2e', muted: '#5a7a7a', shadow: '0 2px 10px rgba(0,0,0,0.07)',
  danger: '#ef4444',
};

const GST_OPTIONS = [0, 1.5, 2.5, 6, 9, 14];

// ── Shared react-select styles ────────────────────────────────────────────────
const rsStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '30px', height: '30px', fontSize: '12px',
    borderRadius: '7px', backgroundColor: '#fafafa',
    borderColor: state.isFocused ? C.accent : C.cardBorder,
    boxShadow: state.isFocused ? `0 0 0 1.5px ${C.accent}` : 'none',
    '&:hover': { borderColor: C.accentBorder },
  }),
  valueContainer: (base) => ({ ...base, padding: '0 8px' }),
  input: (base) => ({ ...base, fontSize: '12px', margin: 0, padding: 0 }),
  singleValue: (base) => ({ ...base, fontSize: '12px' }),
  option: (base, state) => ({
    ...base, fontSize: '12px',
    backgroundColor: state.isSelected ? C.accent : state.isFocused ? C.accentDim : '#fff',
    color: state.isSelected ? '#fff' : C.text,
  }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

// ── Reusable text field ───────────────────────────────────────────────────────
const Field = ({ label, name, value, onChange, placeholder, type = 'text',
  required, align, startIcon, maxLength }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
    <Typography sx={{ fontSize: '9.5px', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
      {label}{required && <span style={{ color: C.danger, marginLeft: 2 }}>*</span>}
    </Typography>
    <TextField
      size="small" name={name} value={value ?? ''} onChange={onChange}
      placeholder={placeholder || label} type={type} required={required}
      inputProps={{ maxLength, style: { fontSize: '12px', textAlign: align || 'left', padding: '5px 8px' } }}
      InputProps={startIcon ? { startAdornment: <InputAdornment position="start">{startIcon}</InputAdornment> } : undefined}
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: '7px', bgcolor: '#fafafa',
          '& fieldset': { borderColor: C.cardBorder },
          '&:hover fieldset': { borderColor: C.accentBorder },
          '&.Mui-focused fieldset': { borderColor: C.accent, borderWidth: 1.5 },
        },
      }}
    />
  </Box>
);

// ── GST select ────────────────────────────────────────────────────────────────
const GSTField = ({ label, name, value, onChange }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
    <Typography sx={{ fontSize: '9.5px', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
      {label}
    </Typography>
    <FormControl size="small">
      <MuiSelect name={name} value={value ?? 0} onChange={onChange}
        sx={{
          fontSize: '12px', borderRadius: '7px', bgcolor: '#fafafa',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: C.cardBorder },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: C.accentBorder },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: C.accent, borderWidth: 1.5 },
          '& .MuiSelect-select': { padding: '5px 8px', fontSize: '12px' },
        }}>
        {GST_OPTIONS.map(v => <MenuItem key={v} value={v} sx={{ fontSize: '12px' }}>{v}</MenuItem>)}
      </MuiSelect>
    </FormControl>
  </Box>
);

// ── ReactSelect label field ───────────────────────────────────────────────────
const SelectField = ({ label, required, children }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
    <Typography sx={{ fontSize: '9.5px', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
      {label}{required && <span style={{ color: C.danger, marginLeft: 2 }}>*</span>}
    </Typography>
    {children}
  </Box>
);

// ── Section card ──────────────────────────────────────────────────────────────
const Section = ({ title, icon, children, color = C.accent }) => (
  <Box sx={{
    bgcolor: C.card, borderRadius: '10px', boxShadow: C.shadow,
    border: `1px solid ${C.cardBorder}`, overflow: 'hidden',
  }}>
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 0.8,
      px: '12px', py: '7px', borderBottom: `1px solid ${C.cardBorder}`,
      bgcolor: `${color}0d`,
    }}>
      <Box sx={{ color, fontSize: 15, display: 'flex' }}>{icon}</Box>
      <Typography sx={{ fontSize: '10px', fontWeight: 700, color: C.text, textTransform: 'uppercase', letterSpacing: '0.6px' }}>
        {title}
      </Typography>
    </Box>
    <Box sx={{ p: '10px 12px', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
      {children}
    </Box>
  </Box>
);

// ── Main component ────────────────────────────────────────────────────────────
const ItemCreate = () => {
  const history = useHistory();
  const { isAuthenticated, companyId, userId, logout, token } = useAuth();
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  useEffect(() => {
    if (isAuthenticated && tokenIsExpired(token)) { logout(); }
  }, [isAuthenticated, token, logout]);

  const [formData, setFormData] = useState({
    CompanyId: companyId,
    Code: '', BarCode: '', Name: '', SearchableName: '',
    CategoryId: '0', UnitofMeasureId: '0', SupplierId: '0',
    MRP: '0', SaleRate: '0', WholeSaleRate: '0', PurchaseRate: '0',
    ReOrderLevelQty: '0', CGSTRate: 0, SGSTRate: 0,
    HSNCode: '', OpeningStock: '0',
    CreatedBy: userId, CreatedDate: new Date().toISOString(),
  });

  const [selectedCategory, setSelectedCategory]   = useState(null);
  const [selectedUOM, setSelectedUOM]             = useState(null);
  const [selectedSupplier, setSelectedSupplier]   = useState(null);
  const [categoryOptions, setCategoryOptions]     = useState([]);
  const [uomOptions, setUomOptions]               = useState([]);
  const [supplierOptions, setSupplierOptions]     = useState([]);

  useEffect(() => {
    // Wait until token and companyId are ready
    if (!token || !companyId) return;

    const load = async () => {
      try {
        const [catRes, uomRes, supRes] = await Promise.all([
          fetchData('ItemApi', `/Category/${companyId}`, token),
          fetchData('ItemApi', `/uom/${companyId}`, token),
          fetchData('ItemApi', `/supplier/${companyId}`, token),
        ]);

        if (catRes.responseCode === '1111')
          setCategoryOptions(catRes.response_data.map(c => ({ value: c.Id, label: c.Name })));
        else
          console.warn('Category API unexpected response:', catRes);

        if (uomRes.responseCode === '1111')
          setUomOptions(uomRes.response_data.map(u => ({ value: u.Id, label: u.Unit })));
        else
          console.warn('UOM API unexpected response:', uomRes);

        if (supRes.responseCode === '1111')
          setSupplierOptions(supRes.response_data.map(s => ({ value: s.Id, label: s.Name })));
        else
          console.warn('Supplier API unexpected response:', supRes);

      } catch (err) {
        console.error('Error loading dropdowns:', err);
      }
    };

    load();
  }, [companyId, token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!formData.Name.trim())    { setError('Name is required.'); return; }
    if (!formData.Code.trim())    { setError('Code is required.'); return; }
    if (!formData.HSNCode.trim()) { setError('HSN Code is required.'); return; }
    if (parseFloat(formData.CGSTRate) !== parseFloat(formData.SGSTRate)) {
      setError('CGST and SGST rates must be equal.'); return;
    }
    if (!selectedCategory?.value) { setError('Please select a category.'); return; }
    if (!selectedUOM?.value)      { setError('Please select a unit of measure.'); return; }

    setSaving(true);
    try {
      await postData('ItemApi', {
        ...formData,
        CategoryId: selectedCategory.value,
        UnitofMeasureId: selectedUOM.value,
        SupplierId: selectedSupplier?.value ?? 0,
        CreatedBy: userId,
        CompanyId: companyId,
      }, token);
      history.push('/ItemIndex');
    } catch (err) {
      console.error('Error creating item:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ bgcolor: C.bg, p: '6px 6px 6px' }}>

      {/* ── Page header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <InventoryIcon sx={{ fontSize: 16, color: C.accent }} />
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 800, fontSize: '13px', color: C.text }}>
            Item Create
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.8 }}>
          <Button component={Link} to="/ItemIndex" size="small"
            startIcon={<ArrowBackIcon sx={{ fontSize: 13 }} />}
            sx={{
              fontSize: '11px', fontWeight: 600, textTransform: 'none',
              color: C.muted, bgcolor: C.card, border: `1px solid ${C.cardBorder}`,
              borderRadius: '7px', px: 1.2, py: 0.4, boxShadow: C.shadow,
              '&:hover': { bgcolor: C.accentDim, borderColor: C.accentBorder, color: C.text },
            }}>
            Back
          </Button>
          <Button onClick={handleSubmit} disabled={saving} size="small"
            startIcon={saving ? <CircularProgress size={11} sx={{ color: '#fff' }} /> : <SaveIcon sx={{ fontSize: 13 }} />}
            sx={{
              fontSize: '11px', fontWeight: 700, textTransform: 'none',
              bgcolor: C.accent, color: '#fff', borderRadius: '7px',
              px: 1.4, py: 0.4, boxShadow: C.shadow,
              '&:hover': { bgcolor: C.accentDark },
              '&:disabled': { bgcolor: C.accentBorder },
            }}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </Box>
      </Box>

      {error && (
        <Box sx={{ mb: 1, px: '10px', py: '6px', bgcolor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '7px' }}>
          <Typography sx={{ fontSize: '11px', color: C.danger }}>{error}</Typography>
        </Box>
      )}

      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>

          {/* ── Basic Info ── */}
          <Section title="Basic Information" icon={<LabelIcon />}>
            <Box sx={{ flex: '1 1 100px' }}>
              <Field label="Code" name="Code" value={formData.Code}
                onChange={handleChange} required maxLength={10} />
            </Box>
            <Box sx={{ flex: '1 1 120px' }}>
              <Field label="Bar Code" name="BarCode" value={formData.BarCode}
                onChange={handleChange} maxLength={20} />
            </Box>
            <Box sx={{ flex: '2 1 220px' }}>
              <Field label="Name" name="Name" value={formData.Name}
                onChange={handleChange} required />
            </Box>
            <Box sx={{ flex: '2 1 220px' }}>
              <Field label="Searchable Name" name="SearchableName" value={formData.SearchableName}
                onChange={handleChange} />
            </Box>
          </Section>

          {/* ── Classification ── */}
          <Section title="Classification" icon={<QrCodeIcon />} color="#6366f1">
            <Box sx={{ flex: '1 1 180px' }}>
              <SelectField label="Category" required>
                <Select value={selectedCategory} onChange={setSelectedCategory}
                  options={categoryOptions} placeholder="Select category…"
                  isSearchable styles={rsStyles}
                  menuPortalTarget={document.body} menuPosition="fixed" />
              </SelectField>
            </Box>
            <Box sx={{ flex: '1 1 120px' }}>
              <SelectField label="Unit of Measure" required>
                <Select value={selectedUOM} onChange={setSelectedUOM}
                  options={uomOptions} placeholder="Select UOM…"
                  isSearchable styles={rsStyles}
                  menuPortalTarget={document.body} menuPosition="fixed" />
              </SelectField>
            </Box>
            <Box sx={{ flex: '1 1 180px' }}>
              <SelectField label="Supplier">
                <Select value={selectedSupplier} onChange={setSelectedSupplier}
                  options={supplierOptions} placeholder="Select supplier…"
                  isSearchable styles={rsStyles}
                  menuPortalTarget={document.body} menuPosition="fixed" />
              </SelectField>
            </Box>
            <Box sx={{ flex: '1 1 120px' }}>
              <Field label="HSN Code" name="HSNCode" value={formData.HSNCode}
                onChange={handleChange} required maxLength={20}
                startIcon={<QrCodeIcon sx={{ fontSize: 13, color: C.muted }} />} />
            </Box>
          </Section>

          {/* ── GST Rates ── */}
          <Section title="GST Rates" icon={<PercentIcon />} color="#f59e0b">
            <Box sx={{ flex: '1 1 110px' }}>
              <GSTField label="CGST Rate" name="CGSTRate" value={formData.CGSTRate} onChange={handleChange} />
            </Box>
            <Box sx={{ flex: '1 1 110px' }}>
              <GSTField label="SGST Rate" name="SGSTRate" value={formData.SGSTRate} onChange={handleChange} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', pb: '2px' }}>
              <Typography sx={{ fontSize: '11px', color: C.muted }}>
                Total GST: <strong style={{ color: C.text }}>
                  {Number(formData.CGSTRate) + Number(formData.SGSTRate)}%
                </strong>
              </Typography>
            </Box>
          </Section>

          {/* ── Pricing ── */}
          <Section title="Pricing & Stock" icon={<AttachMoneyIcon />} color="#ec4899">
            <Box sx={{ flex: '1 1 110px' }}>
              <Field label="Purchase Rate" name="PurchaseRate" value={formData.PurchaseRate}
                onChange={handleChange} type="number" align="right" />
            </Box>
            <Box sx={{ flex: '1 1 110px' }}>
              <Field label="MRP" name="MRP" value={formData.MRP}
                onChange={handleChange} type="number" align="right" />
            </Box>
            <Box sx={{ flex: '1 1 110px' }}>
              <Field label="Sale Rate" name="SaleRate" value={formData.SaleRate}
                onChange={handleChange} type="number" align="right" />
            </Box>
            <Box sx={{ flex: '1 1 110px' }}>
              <Field label="Wholesale Rate" name="WholeSaleRate" value={formData.WholeSaleRate}
                onChange={handleChange} type="number" align="right" />
            </Box>
            <Box sx={{ flex: '1 1 110px' }}>
              <Field label="Opening Stock" name="OpeningStock" value={formData.OpeningStock}
                onChange={handleChange} type="number" align="right" />
            </Box>
            <Box sx={{ flex: '1 1 110px' }}>
              <Field label="Re-Order Level" name="ReOrderLevelQty" value={formData.ReOrderLevelQty}
                onChange={handleChange} type="number" align="right" />
            </Box>
          </Section>

        </Box>
      </form>
    </Box>
  );
};

export default ItemCreate;

